import { Object3D, PMREMGenerator, RoomEnvironment, type SceneControl, use } from '@anov/3d-core'
import { SkyOptions, HDROptions, ColorOptions, WeatherSystemOptions, BaseController, WeatherOptions, IHDRController, IColorController, ISkyController, ControllerType} from './types'
import { SkyController} from './controller/SkyController'
import { HDRController } from './controller/HDRController'
import { ColorController } from './controller/ColorController'


const DEFAULT_OPTIONS: WeatherSystemOptions = {
  mode: 'sky',
}
export class WeatherSystem extends Object3D  {
  private current?: ControllerType
  private sceneControl?: SceneControl

  constructor(options: WeatherSystemOptions = DEFAULT_OPTIONS, sceneControl?: SceneControl) {
    super()
    this.sceneControl = sceneControl

    this.current = this.createController(options)

    if (this.current.object) this.add(this.current.object)

    this.current.enter()
  }

  async setMode<T extends ControllerType>(options: WeatherSystemOptions): Promise<T | undefined> {
    const {envManager} = use.useScene(this.sceneControl)
    // 模式相同 → 更新参数
    if (this.current?.mode === options.mode) {
      await this.current.update?.(options as any)
      return this.current as T
    }
    // 不同模式：切换
    if (this.current) {
      this.current.exit()
      if (this.current.object)
        this.remove(this.current.object)
    }

    this.current = this.createController(options)

    if (this.current.object) this.add(this.current.object)

    await this.current.enter()

    return this.current as T
  }


  private createController(options: WeatherSystemOptions): ControllerType{
    const { mode, ...Options } = options
    switch (options.mode) {
      case 'sky':
        return new SkyController(Options as SkyOptions, this.sceneControl)
      case 'hdr':
        return new HDRController(Options as HDROptions, this.sceneControl)
      case 'color':
        return new ColorController(Options as ColorOptions, this.sceneControl)
    }
  }
  /**  ---------------------- 当前模式对象可访问--------------------- */
  get mode() {
    return this.current?.mode
  }
  get currentObject(){
    return this.current?.object
  }

  get time() {
    if (this.checkModeSupport('time', 'sky')) {
      return (this.current as ISkyController).time
    }
  }
  get weather() {
    if (this.checkModeSupport('weather', 'sky')) {
      return (this.current as ISkyController).weather
    }
  }
  set rotate(rotate: number){
    if(this.checkModeSupport('rotate', 'hdr')){
      (this.current as IHDRController).rotate = rotate
    }
  }
  set intensity(intensity: number){

    if((this.mode == 'hdr') && this.checkModeSupport('intensity', 'hdr')){
      (this.current as IHDRController).intensity = intensity
    }
    if((this.mode == 'color') && this.checkModeSupport('intensity', 'color')){
      (this.current as IColorController).intensity = intensity
    }
  }
  set path(path: string){
    if(this.checkModeSupport('path', 'hdr')){
      (this.current as IHDRController).path = path
    }
  }
  set color(color: number | string){
    if(this.checkModeSupport('color', 'color')){
      (this.current as IColorController).color = color
    }
  }
  set alpha(alpha: number){
    if(this.checkModeSupport('alpha', 'color')){
      (this.current as IColorController).alpha = alpha
    }
  }
  /**  ---------------------- 外部可调用API--------------------- */
  async useSky(option: SkyOptions = {}) {
    return await this.setMode({ mode: 'sky', ...option })
  }
  async useHDR(option: HDROptions) {
    return await this.setMode({ mode: 'hdr', ...option })
  }
  async useColor(option: ColorOptions = {}) {
    return await this.setMode({ mode: 'color', ...option })
  }
  getController() {
    return this.current
  }

  setTime(time: string) {
    this.call('setTime', time)
  }
  setWeather(weather: string) {
    this.call('setWeather', weather)
  }
  

  private async  call<K extends keyof ControllerType>(
    method: K,
    ...args: any[]
  ) {
    const ctrl = this.current as any
    if (ctrl && typeof ctrl[method] === 'function') {
      await ctrl[method](...args)
    } else {
      console.warn(`当前模式 ${this.mode} 不支持 ${String(method)}`)
    }
  }
  private checkModeSupport(property: string, supportedMode: string): boolean {
    if (!this.current || this.current.mode !== supportedMode) {
      console.warn(`当前模式 ${this.mode} 不支持设置/获取 ${property}`)
      return false
    }
    return true
  }

}
